/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import Renderable from "App/render/webgl/renderables/renderable";
import MultiArrayBufferHandler from "App/render/webgl/utils/multi_array_buffer_handler";
import ElementArrayBufferHandler from "App/render/webgl/utils/element_array_buffer_handler";
import WebGLRenderGraph from "App/render/webgl/render_graph";
import ShapeBatchNode from "App/render/webgl/nodes/shape_batch_node";
import WebGLContext from "App/web_gl_context";
import Canvas2DContext from "App/canvas_2d_context";
import ArrayBufferHandler from "App/render/webgl/utils/array_buffer_handler";
import AbstractGeometry from "App/geometry/objects_geometry/abstract_geometry";
import {logError} from "App/debug";
import Shape from "App/geometry/objects/shape";
import LinkedShapesListItem from "App/render/webgl/utils/linked_shapes_list_item";
import Pass from "App/render/webgl/passes/pass";
import IScene from "js_interface/src/scene_interface";

export enum UPDATETYPES {
    VERTEX = 0,
    NORMAL = 1,
    TEXTURE = 2,
    VISIBILITY = 3,
    SELECTION = 4,
    MANIPULATION = 5,
    OBJECTSIDS = 6,
    DISTANCES = 7, // lines
    COLOR = 8, // point cloud
    POSITION_OFFSET = 9, // for billboards
    LAYERS = 10, // for volumetric indexed face set (chunk lod node)
    NONINDEXED_VERTEX = 11,
    NONINDEXED_NORMAL = 12,
    NONINDEXED_MANIPULATON = 13,
    NONINDEXED_PRIMITIVEINDEX = 14,
    INDICES = 15
}

export default
class Batch extends Renderable {

    public static TypeName: string = "Batch";

    protected static Float32BufferSize = 512 * 512;
    private static UintBufferSize = 100000;
    protected static Float32Buffer0 = Batch.createAndInitArray(Batch.Float32BufferSize, 0);
    protected static Float32Buffer1 = Batch.createAndInitArray(Batch.Float32BufferSize, 1);
    protected static Float32Buffer = new Float32Array(Batch.Float32BufferSize);
    private static Uint32Buffer = new Uint32Array(Batch.UintBufferSize);
    private static Uint16Buffer = new Uint16Array(Batch.UintBufferSize);

    protected _attributes: MultiArrayBufferHandler;
    protected _indices: ElementArrayBufferHandler;
    protected _verticesCount: number;
    protected _indicesCount: number;
    protected shapeBatchNode: ShapeBatchNode;
    protected _indicesType: GLenum;
    protected _indicesTypeSize: number;

    constructor(aSceneAPI, aRenderGraph, aShapeBatchNode) {
        super(aSceneAPI, aRenderGraph, aShapeBatchNode);

        this._attributes = new MultiArrayBufferHandler(this);
        this._indices = new ElementArrayBufferHandler(this);

        this._verticesCount = 0;
        this._indicesCount = 0;

        this.shapeBatchNode = aShapeBatchNode;

        this._typeName = Batch.TypeName;
    }


    public static create(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph, aShapeBatchNode: ShapeBatchNode) {
        return new Batch(aSceneAPI, aRenderGraph, aShapeBatchNode);
    }

    public static createAndInitArray(aBufferSize: number, aInitValue: number): Float32Array {
        let dataBuffer = new Float32Array(aBufferSize);

        if (aInitValue !== 0) {
            if (dataBuffer.fill !== undefined) {
                dataBuffer.fill(aInitValue);
            } else {
                for (let i = 0; i < aBufferSize; i++) {
                    dataBuffer[i] = aInitValue;
                }
            }
        }

        return dataBuffer;
    }

    public getContext(): WebGLContext | Canvas2DContext {
        return this._context;
    }

    public release() {
        this._attributes.release();
        this._indices.release();
    }

    public checkVerticesFitToBuffers(aVerticesCount: number) {
        return this._attributes.canHandleVertices(aVerticesCount);
    }

    public reCreate(aVerticesCount: number, aIndicesCount: number) {
        if ((aVerticesCount > ArrayBufferHandler.maxVerticesCount) &&
            WebGLContext.getFeatureAvailable("INDEX_UINT")) {
            this._indicesType = (<WebGLContext>this._context).getWebGLRenderingContext().UNSIGNED_INT;
            this._indicesTypeSize = 4;
        } else {
            this._indicesType = (<WebGLContext>this._context).getWebGLRenderingContext().UNSIGNED_SHORT;
            this._indicesTypeSize = 2;
        }

        this._verticesCount = aVerticesCount;
        this._indicesCount = aIndicesCount;
    }

    public updateVertices(aPlacement: LinkedShapesListItem, aGeometry: AbstractGeometry, aMemberIndex: number) {
        if (!aPlacement.placed) {
            aPlacement.buffer.vertexOffset = this._attributes.setAndGetNextFreePosition(<Float32Array>aGeometry.positionsDesc.data, aMemberIndex);
        }

        this.updateAttributes(aPlacement.buffer.vertexOffset, <Float32Array>aGeometry.positionsDesc.data, aMemberIndex);
    }

    public updateNonIndexedVertices(aPlacement: LinkedShapesListItem, aData: Float32Array, aMemberIndex: number
    ) {
        if (!aData || aData.length === 0) {
            logError("updateNonIndexedVertices: empty data");
            return;
        }

        if (!aPlacement.placed) {
            aPlacement.buffer.nonIndexedVertexOffset = this._attributes.setAndGetNextFreePosition(aData, aMemberIndex);
        }

        this.updateAttributes(aPlacement.buffer.nonIndexedVertexOffset, aData, aMemberIndex);
    }

    public updateAttributes(aInMemberOffset: number, aData: Float32Array, aMemberIndex: number) {
        if (aData && aData.length > 0) {
            this._attributes.update(aData, aInMemberOffset, aMemberIndex);
        }
    }

    private _computeOffsetedIndices(aIndices: Uint32Array, aOffset: number, aIndicesTypeSize: number): Uint32Array | Uint16Array {
        let offsetedIndices = null;
        if (Batch.UintBufferSize < aIndices.length) {
            offsetedIndices = aIndicesTypeSize === 4 ? new Uint32Array(aIndices.length) : new Uint16Array(aIndices.length);
        } else {
            // 4 stands for four bytes i.e. 2^32 values
            if (aIndicesTypeSize === 4) {
                offsetedIndices = new Uint32Array(Batch.Uint32Buffer.buffer, 0, aIndices.length);
            } else {
                offsetedIndices = new Uint16Array(Batch.Uint16Buffer.buffer, 0, aIndices.length);
            }

        }

        let len = aIndices.length;
        for (let i = 0; i < len; i++) {
            offsetedIndices[i] = aIndices[i] + aOffset;
        }

        // TODO: can we release the not offseted indices? NO here! because we need the indices to create
        //       the nonindexed buffers for primitive id picking

        return offsetedIndices;
    }

    public updateIndices(aPlacement: LinkedShapesListItem, aGeometry: AbstractGeometry, aIndicesTypeSize: number) {
        if (aPlacement.buffer.indexOffset < 0) {
            let offsetedIndices = this._computeOffsetedIndices(<Uint32Array>aGeometry.indicesDesc.data, aPlacement.buffer.vertexOffset, aIndicesTypeSize);

            aPlacement.buffer.indexOffset = this._indices.setAndGetNextFreePosition(offsetedIndices.length);

            this._indices.update(offsetedIndices, aPlacement.buffer.indexOffset);
        }
    }

    public computeNonIndexedNormals(aShape: Shape) {
        let indicesCount = aShape.getIndicesCount();
        let geometry = aShape.getGeometry();
        let indexedNormalsArray = geometry.normalsDesc.data;
        let nonIndexedNormalsArray = new Float32Array(indicesCount * 3); // * aPrimitiveSize == e.g. xyz

        let indexedNormalIndex = 0;
        for (let inIndex = 0, outIndex = 0; inIndex < indicesCount; inIndex++, outIndex += 3) {
            indexedNormalIndex = geometry.indicesDesc.data[inIndex];
            for (let pi = 0; pi < 3; pi++) {
                nonIndexedNormalsArray[outIndex + pi] = indexedNormalsArray[indexedNormalIndex + pi];
            }
        }

        return nonIndexedNormalsArray;
    }

    public computePrimitiveIndices(aShape: Shape) {
        // indices count equals vertices count here
        let primitiveSize = aShape.getPrimitiveSize();
        let indicesCount = aShape.getIndicesCount();
        let primitiveIndexBuffer = new Float32Array(indicesCount);
        for (let inIndex = 0; inIndex < indicesCount; inIndex++) {
            primitiveIndexBuffer[inIndex] = Math.floor(inIndex / primitiveSize) + 1; // +1 == start counting the primitives from 1 because of the 0 stands for no picked primitive
        }

        return primitiveIndexBuffer;
    }

    public renderPrimitiveId(aRenderPass: Pass, aPlacement: LinkedShapesListItem, aLength: number) {
    }
}

